import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Tabs, Tab, TextField, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Pagination, CircularProgress, Chip, Button } from '@mui/material';
import { Logout } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { adminService } from '../../services/adminService';

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return <Box sx={{ mt: 2 }}>{children}</Box>;
}

const AdminDashboard = () => {
  const { logout } = useAuth();
  const [tab, setTab] = useState(0);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ items: [], pagination: { current: 1, pages: 1, total: 0 } });

  const handleLogout = () => logout();

  const fetchers = [
    (p) => adminService.getUsers({ q, page: p, limit: 10 }),
    (p) => adminService.getPharmacies({ q, page: p, limit: 10 }),
    (p) => adminService.getCustomers({ q, page: p, limit: 10 }),
    (p) => adminService.getRiders({ q, page: p, limit: 10 }),
    (p) => adminService.getOrders({ q, page: p, limit: 10 }),
    (p) => adminService.getPayments({ q, page: p, limit: 10 })
  ];

  const load = async (p = 1) => {
    setLoading(true);
    try {
      const res = await fetchers[tab](p);
      setData(res);
      setPage(res.pagination?.current || p);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleSearchKey = (e) => {
    if (e.key === 'Enter') load(1);
  };

  const renderTable = () => {
    if (tab === 0) {
      return (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>User Type</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Last Login</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((u) => (
              <TableRow key={u._id}>
                <TableCell>{u.email}</TableCell>
                <TableCell>{u.userType}</TableCell>
                <TableCell>{u.isActive ? 'Yes' : 'No'}</TableCell>
                <TableCell>{u.lastLogin ? new Date(u.lastLogin).toLocaleString() : '-'}</TableCell>
                <TableCell>{u.createdAt ? new Date(u.createdAt).toLocaleString() : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }
    if (tab === 1) {
      return (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Pharmacy</TableCell>
              <TableCell>License</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>City</TableCell>
              <TableCell>Verified</TableCell>
              <TableCell>User Email</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((p) => (
              <TableRow key={p._id}>
                <TableCell>{p.pharmacyName}</TableCell>
                <TableCell>{p.licenseNumber}</TableCell>
                <TableCell>{p.contactInfo?.phone}</TableCell>
                <TableCell>{p.address?.city}</TableCell>
                <TableCell>{p.isVerified ? 'Yes' : 'No'}</TableCell>
                <TableCell>{p.userId?.email}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }
    if (tab === 2) {
      return (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>First Name</TableCell>
              <TableCell>Last Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>City</TableCell>
              <TableCell>User Email</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((c) => (
              <TableRow key={c._id}>
                <TableCell>{c.firstName}</TableCell>
                <TableCell>{c.lastName}</TableCell>
                <TableCell>{c.phone}</TableCell>
                <TableCell>{c.address?.city}</TableCell>
                <TableCell>{c.userId?.email}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }
    if (tab === 3) {
      return (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>First Name</TableCell>
              <TableCell>Last Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Available</TableCell>
              <TableCell>User Email</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((r) => (
              <TableRow key={r._id}>
                <TableCell>{r.firstName}</TableCell>
                <TableCell>{r.lastName}</TableCell>
                <TableCell>{r.phone}</TableCell>
                <TableCell>{r.isAvailable ? 'Yes' : 'No'}</TableCell>
                <TableCell>{r.userId?.email}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }
    // Orders
    if (tab === 4) {
      const getPaymentStatusColor = (status) => {
        switch (status) {
          case 'paid': return 'success';
          case 'pending': return 'warning';
          case 'failed': return 'error';
          default: return 'default';
        }
      };

      const getOrderStatusColor = (status) => {
        switch (status) {
          case 'delivered': return 'success';
          case 'cancelled': return 'error';
          case 'pending': return 'warning';
          default: return 'default';
        }
      };

      return (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Order #</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment Status</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Pharmacy</TableCell>
              <TableCell>Rider</TableCell>
              <TableCell>Paid At</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((o) => (
              <TableRow key={o._id}>
                <TableCell>{o.orderNumber || o._id.slice(-8)}</TableCell>
                <TableCell>
                  <Chip 
                    label={o.status || 'pending'} 
                    size="small" 
                    color={getOrderStatusColor(o.status)}
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={o.paymentStatus || 'pending'} 
                    size="small" 
                    color={getPaymentStatusColor(o.paymentStatus)}
                  />
                </TableCell>
                <TableCell>{o.paymentMethod || '-'}</TableCell>
                <TableCell>
                  {o.currency === 'NGN' ? '₦' : '$'}{o.totalAmount?.toLocaleString() || '0'}
                </TableCell>
                <TableCell>{o.customerId ? `${o.customerId.firstName} ${o.customerId.lastName}` : '-'}</TableCell>
                <TableCell>{o.pharmacyId ? o.pharmacyId.pharmacyName : '-'}</TableCell>
                <TableCell>{o.riderId ? `${o.riderId.firstName} ${o.riderId.lastName}` : '-'}</TableCell>
                <TableCell>{o.paidAt ? new Date(o.paidAt).toLocaleString() : '-'}</TableCell>
                <TableCell>{o.createdAt ? new Date(o.createdAt).toLocaleString() : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }
    // Payments
    if (tab === 5) {
      const getPaymentStatusColor = (status) => {
        switch (status) {
          case 'paid': return 'success';
          case 'pending': return 'warning';
          case 'failed': return 'error';
          default: return 'default';
        }
      };

      return (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Reference</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Orders Count</TableCell>
              <TableCell>Paid At</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((p) => (
              <TableRow key={p._id}>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  {p.reference}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={p.status || 'pending'} 
                    size="small" 
                    color={getPaymentStatusColor(p.status)}
                  />
                </TableCell>
                <TableCell>
                  {p.currency === 'NGN' ? '₦' : '$'}{p.amount?.toLocaleString() || '0'}
                </TableCell>
                <TableCell>{p.currency || 'NGN'}</TableCell>
                <TableCell>{p.provider || 'paystack'}</TableCell>
                <TableCell>
                  {p.customerId 
                    ? `${p.customerId.firstName || ''} ${p.customerId.lastName || ''}`.trim() || p.customerId.userId?.email || '-'
                    : '-'
                  }
                </TableCell>
                <TableCell>{p.orderIds?.length || 0}</TableCell>
                <TableCell>{p.paidAt ? new Date(p.paidAt).toLocaleString() : '-'}</TableCell>
                <TableCell>{p.createdAt ? new Date(p.createdAt).toLocaleString() : '-'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }
    return null;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Typography variant="h4">Admin Dashboard</Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Logout />}
          onClick={handleLogout}
        >
          Log out
        </Button>
      </Box>
      <Card>
        <CardContent>
          <Tabs value={tab} onChange={(e, v) => { setTab(v); setQ(''); setData({ items: [], pagination: { current: 1, pages: 1, total: 0 } }); }}>
            <Tab label="Users" />
            <Tab label="Pharmacies" />
            <Tab label="Customers" />
            <Tab label="Riders" />
            <Tab label="Orders" />
            <Tab label="Payments" />
          </Tabs>
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField fullWidth placeholder="Search..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={handleSearchKey} />
          </Box>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer sx={{ mt: 2 }}>
              {renderTable()}
            </TableContainer>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Pagination count={data.pagination?.pages || 1} page={page} onChange={(_, p) => load(p)} color="primary" />
          </Box>
        </CardContent>
      </Card>

      <TabPanel value={tab} index={-1} />
    </Box>
  );
};

export default AdminDashboard;


